import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertCircle } from 'lucide-react';
import { Button, Input, Logo } from '../components/common';
import { ServerWakeUpBanner } from '../components/common/ServerWakeUpBanner';
import { useLogin } from '../hooks/useAuth';

/** 로그인 에러 메시지 분류 (네트워크 에러 vs 인증 에러) */
const getLoginErrorMessage = (error: unknown): string => {
  const err = error as any;
  if (err?.code === 'ERR_NETWORK') {
    return `서버에 연결할 수 없습니다. 인터넷 연결 또는 서버 URL 설정을 확인해주세요. (${err.message || 'ERR_NETWORK'})`;
  }
  if (err?.code === 'ECONNABORTED') {
    return `서버 응답이 지연되고 있습니다. 잠시 후 다시 시도해주세요. (${err.message || 'TIMEOUT'})`;
  }
  const status = err?.response?.status;
  if (status === 502 || status === 503) {
    return '서버가 일시적으로 응답하지 않습니다. 잠시 후 다시 시도해주세요. (Fly.io Proxy Error)';
  }
  return (
    err?.response?.data?.error?.message ||
    err?.message ||
    '로그인에 실패했습니다. 이메일과 비밀번호를 확인해주세요.'
  );
};

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const loginMutation = useLogin();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    loginMutation.mutate({ email, password });
  };

  return (
    <div className="hell-scope relative min-h-screen flex items-center justify-center bg-[var(--bg)] p-4 overflow-hidden font-sans">
      {/* 1. 사이버펑크 붉은 격자(Red Grid) 배경 */}
      <div 
        className="absolute inset-0 pointer-events-none opacity-[0.12]"
        style={{
          backgroundImage: `
            linear-gradient(to right, rgba(239, 68, 68, 0.15) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(239, 68, 68, 0.15) 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
        }}
      />

      {/* 2. 어두운 그라데이션 및 비네팅 오버레이 */}
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_center,transparent_20%,var(--bg)_100%)] z-1" />

      {/* 3. 강렬한 레드/오렌지 글로우 오브 */}
      <div className="absolute top-1/4 left-1/4 -translate-x-1/2 -translate-y-1/2 w-[35rem] h-[35rem] bg-[var(--accent)]/10 rounded-full blur-[140px] pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 translate-x-1/2 translate-y-1/2 w-[35rem] h-[35rem] bg-[var(--accent)]/5 rounded-full blur-[140px] pointer-events-none" />

      <motion.div
        className="relative w-full max-w-md z-10"
        initial={{ opacity: 0, y: 35 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      >
        {/* 데코 사선 프레임 */}
        <div className="absolute -top-3 -left-3 w-8 h-8 border-t-2 border-l-2 border-[var(--accent)] pointer-events-none opacity-80" />
        <div className="absolute -bottom-3 -right-3 w-8 h-8 border-b-2 border-r-2 border-[var(--accent)] pointer-events-none opacity-80" />

        {/* 메인 카드 */}
        <div className="backdrop-blur-xl bg-[var(--card)]/80 rounded-3xl p-8 border border-[var(--border)] shadow-[0_0_60px_rgba(0,0,0,0.8),inset_0_1px_1px_rgba(255,255,255,0.05)]">
          
          {/* 로고 */}
          <div className="flex flex-col items-center mb-6">
            <div className="drop-shadow-[0_0_15px_rgba(239,68,68,0.4)]">
              <Logo size="lg" />
            </div>
          </div>

          {/* 한글 소개 헤더 */}
          <div className="text-center mb-6">
            <h2 className="text-3xl font-black tracking-tight text-[var(--text)] mb-2">
              헬게이트 오픈
            </h2>
            <p className="text-xs text-[var(--text-secondary)] font-bold">
              지옥문 너머, 더 강한 내가 기다린다
            </p>
          </div>

          {/* 서버 상태 표시 배너 */}
          <ServerWakeUpBanner />

          {/* 에러 피드백 */}
          {loginMutation.isError && (
            <motion.div
              className="mb-6 p-4 bg-[var(--danger)]/20 border border-[var(--danger)]/30 rounded-xl flex items-center gap-3 backdrop-blur-md"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <AlertCircle size={18} className="text-[var(--danger)] flex-shrink-0" />
              <p className="text-[var(--danger)] text-xs font-semibold leading-relaxed">
                {getLoginErrorMessage(loginMutation.error)}
              </p>
            </motion.div>
          )}

          {/* 로그인 입력 폼 */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Input
                type="email"
                label="이메일 주소"
                placeholder="email@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="username"
                name="email"
                className="bg-[var(--bg)]/40 border-[var(--border)] text-[var(--text)] placeholder-[var(--text-muted)] focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent)]/20 transition-all duration-300 rounded-xl px-4 py-3 text-sm"
                leftIcon={
                  <svg
                    className="w-4 h-4 text-[var(--text-muted)] transition-colors duration-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"
                    />
                  </svg>
                }
              />
            </div>

            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                label="비밀번호"
                placeholder="비밀번호를 입력하세요"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                name="password"
                className="bg-[var(--bg)]/40 border-[var(--border)] text-[var(--text)] placeholder-[var(--text-muted)] focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent)]/20 transition-all duration-300 rounded-xl px-4 py-3 text-sm"
                leftIcon={
                  <svg
                    className="w-4 h-4 text-[var(--text-muted)] transition-colors duration-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                }
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-[40px] text-[var(--text-muted)] hover:text-[var(--text)] transition-colors"
              >
                {showPassword ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>

            <div className="pt-2">
              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                isLoading={loginMutation.isPending}
                className="bg-gradient-to-r from-[var(--accent)] to-orange-500 hover:from-[var(--accent)] hover:to-orange-400 text-[var(--text)] font-extrabold tracking-widest transition-all duration-300 active:scale-98 rounded-xl py-3 text-sm border-none shadow-[0_0_15px_rgba(239,68,68,0.25)] hover:shadow-[0_0_25px_rgba(239,68,68,0.45)]"
              >
                지옥문 개방 (로그인)
              </Button>
            </div>
          </form>

          {/* 계정 찾기 */}
          <div className="text-center mt-5">
            <Link
              to="/find-account"
              className="text-[var(--text-muted)] hover:text-[var(--accent)] text-xs font-semibold transition-colors"
            >
              계정을 분실하셨나요?
            </Link>
          </div>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[var(--border)]"></div>
            </div>
            <div className="relative flex justify-center text-[10px] tracking-wider uppercase font-black">
              <span className="px-4 bg-[var(--card)] text-[var(--text-muted)]">OR</span>
            </div>
          </div>


          {/* 회원가입 */}
          <div className="text-center">
            <p className="text-xs text-[var(--text-muted)] font-semibold">
              아직 시스템에 등록되지 않으셨나요?{' '}
              <Link
                to="/register"
                className="text-[var(--accent)] hover:opacity-85 font-black transition-colors ml-1 underline"
              >
                지금 무료 회원가입하기
              </Link>
            </p>
          </div>
        </div>

        {/* 하단 텍스트 */}
        <motion.p
          className="text-center text-[var(--text-muted)] text-[10px] tracking-[0.2em] uppercase font-black mt-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          한계를 두려워하지 말라. 헬게이트가 너의 능력을 증명할 것이다.
        </motion.p>
      </motion.div>
    </div>
  );
}
